/**
 * Note creation/edit modal.
 * Supports content, date, and color customization.
 */

import { useState, useEffect, type FormEvent } from 'react';
import type { Note, CreateNoteInput } from '@anna-calendar/shared';
import { DEFAULT_NOTE_COLOR } from '@anna-calendar/shared';
import { Modal } from './Modal';

interface NoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateNoteInput) => void;
  note?: Note | null;
  defaultDate?: string;
}

const COLORS = [
  '#f59e0b', '#ef4444', '#f97316', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899',
];

export function NoteModal({ isOpen, onClose, onSave, note, defaultDate }: NoteModalProps) {
  const [content, setContent] = useState('');
  const [date, setDate] = useState('');
  const [color, setColor] = useState(DEFAULT_NOTE_COLOR);

  useEffect(() => {
    if (note) {
      setContent(note.content);
      setDate(note.date);
      setColor(note.color);
    } else {
      setContent('');
      setDate(defaultDate || new Date().toISOString().slice(0, 10));
      setColor(DEFAULT_NOTE_COLOR);
    }
  }, [note, isOpen, defaultDate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !date) return;

    onSave({
      content: content.trim(),
      date,
      color,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={note ? 'Edit Note' : 'New Note'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full px-3 py-3 rounded-lg text-base outline-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              minHeight: '44px',
            }}
          />
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your note..."
            required
            rows={5}
            className="w-full px-3 py-3 rounded-lg text-base outline-none resize-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            }}
          />
        </div>

        {/* Color picker */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className="w-9 h-9 rounded-full transition-transform"
                style={{
                  backgroundColor: c,
                  transform: color === c ? 'scale(1.2)' : 'scale(1)',
                  border: color === c ? '2px solid var(--text-primary)' : '2px solid transparent',
                }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!content.trim() || !date}
          className="w-full py-3 rounded-lg text-base font-semibold transition-opacity"
          style={{
            backgroundColor: 'var(--accent)',
            color: '#fff',
            opacity: !content.trim() || !date ? 0.5 : 1,
            minHeight: '44px',
          }}
        >
          {note ? 'Save Changes' : 'Create Note'}
        </button>
      </form>
    </Modal>
  );
}
